import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { EvidenceProgressStore } from "../../../../services/evidence-progress-store.service";
import { DebugModeService } from "../../../../services/debug-mode.service";
import {
  EVIDENCE_COPY,
  evidenceDoneTitle,
  evidenceErrorTitle,
  evidenceRunningTitle,
  evidenceRowLabel,
  evidenceStageLabel,
  evidenceStateWord,
  evidenceSubtitle,
  evidenceSummaryLine,
} from "./evidence-progress-copy";
import { formatEvidenceBytes, formatEvidenceClock, formatEvidenceDuration } from "./evidence-format";
import type { IEvidenceBracketView, IEvidenceStageView } from "../../../../model/evidence-progress.types";

/** Appearance threshold (spec L45): the staging row pre-shows as pending after 3 s running. */
const STAGING_PRE_SHOW_MS = 3000;

/** Long-wait threshold (spec L37/L47): the calm note appears after >20 s in a running stage. */
const LONG_WAIT_MS = 20000;

/**
 * Visual copy of one stage row: icon ligature, tone class (component-owned SCSS tint), state
 * word for the icon tooltip and the visible label. Unknown future statuses tolerate their raw
 * string as the tooltip word (calm `radio_button_unchecked` idle presentation). `layer` is the
 * resolved Vexcel layer the backend carries on `api_call` events only (v1.1 S7 — raw data code
 * badge in the shared `notification-badge` neutral variant; null when absent).
 */
interface EvidenceStageRowView {
  stage: string;
  icon: string;
  toneClass: string;
  spin: boolean;
  label: string;
  meta: string | null;
  layer: string | null;
  dimmed: boolean;
  /** True on the currently running row — weight 500 label + the stage-change fade (spec L34/L99). */
  active: boolean;
  tooltip: string;
}

/**
 * Render model of one EPISODE's card block (v1.1 S7 — the store's `view` array is the stacked
 * chronological history; the card renders it in array order) — derived entirely from the
 * store's `IEvidenceBracketView` so the template stays logic-free (spec: "Sin lógica en la
 * plantilla"). `mode` collapses the store's presentation into the two visible shapes: `running`
 * (stage list + live clock) and `summary` (frozen compact line). An errored bracket always
 * renders the calm error header, never a success summary. `kind` is the produced-data code
 * badge (shared `notification-badge` vocabulary, S10) and `repeated` marks a stacked
 * re-execution (occurrence > 0). The `title` is the episode's stable block title (S11): the
 * label or its tool fallback in BOTH modes, with the Done line rendered under it on summary.
 */
interface EvidenceCardView {
  key: string;
  mode: "running" | "summary";
  errored: boolean;
  headerIcon: string;
  headerToneClass: string;
  headerSpin: boolean;
  headerTooltip: string;
  title: string;
  subtitle: string | null;
  kind: string | null;
  repeated: boolean;
  repeatTooltip: string;
  clock: string;
  stages: EvidenceStageRowView[];
  pendingStaging: EvidenceStageRowView | null;
  longNote: string | null;
  summaryWhat: string | null;
  summaryRest: string | null;
  a11y: string;
}

/** Stage row derivation shared by real stage states and the synthetic pending-staging row. */
const stageRowOf = (
  stage: string,
  status: string,
  sizeBytes: number | null,
  durationMs: number | null,
  active: boolean,
  layer: string | null,
): EvidenceStageRowView => {
  let icon: string;
  let toneClass: string;
  if (status === "running") {
    icon = "sync";
    toneClass = "tone-pri";
  } else if (status === "completed") {
    icon = "check_circle";
    toneClass = "tone-ok";
  } else if (status === "error") {
    icon = "error_outline";
    toneClass = "tone-err";
  } else {
    icon = "radio_button_unchecked";
    toneClass = "tone-idle";
  }
  // Magnitude rule (spec L54): size and duration render only on completed stages.
  const meta =
    status === "completed"
      ? [formatEvidenceBytes(sizeBytes), formatEvidenceDuration(durationMs)]
          .filter((part) => part !== "")
          .join(" · ") || null
      : null;
  return {
    stage,
    icon,
    toneClass,
    spin: status === "running",
    label: evidenceStageLabel(stage, status),
    meta,
    layer,
    dimmed: status !== "running" && status !== "completed" && status !== "error",
    active,
    tooltip: evidenceStateWord(status),
  };
};

/**
 * The episode's stable block title (v1.1 S7 running, S11 summary): the exact `label` text when
 * the events carried one, else the tool-based fallback the running header already uses — so
 * the title survives the running→summary transition and the name never disappears on
 * completion (S11). One derivation for both modes.
 */
const episodeTitle = (view: IEvidenceBracketView): string => view.label ?? evidenceRunningTitle(view.bracket.toolId);

/**
 * Standalone DEBUG-ONLY evidence progress card family (evidence-progress-spec.md, user-approved
 * scope decision): renders the ephemeral `evidence_progress` tool-call EPISODES reduced by the
 * container-scoped `EvidenceProgressStore` — the store's `view` array is the stacked
 * chronological episode history (v1.1 S7, oldest first, groups non-contiguous) and the card
 * renders it in array order, one block per episode, each with the running header (state icon,
 * label title or tool fallback, muted subtitle, `m:ss` chronometer), the inner stage rows
 * (`api_call`/`staging` with the raw `layer` code), the staging-pending pre-show, the >20 s
 * calm long-wait note, and the frozen compact summary once the episode closes — the summary
 * keeps the episode title with the Done line under it (S11). Only the live
 * episode of a stacking group can present as running (store contract). The produced-data
 * `kind` rides as a neutral raw-code badge (shared `notification-badge` vocabulary, S10) and
 * episodes with `occurrence > 0` carry a repeat
 * marker; a null `task_id` is a deliberate multi-task (combined) execution, subtitled from the
 * copy deck — never rendered as missing data.
 *
 * Visibility is dual-guarded: this weave auto-guards itself (debug off OR no store OR no
 * visible view → renders nothing), and the `chat-message-list` weave creates the element only
 * under the same triple condition. The card injects `EvidenceProgressStore` optionally so it
 * degrades to nothing in any host without the container-scoped provider (tests, dialogs).
 */
@Component({
  selector: "app-evidence-progress-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatTooltip],
  templateUrl: "./evidence-progress-card.component.html",
  styleUrl: "./evidence-progress-card.component.scss",
})
export class EvidenceProgressCardComponent {
  /** Optional: container-scoped evidence store (chat-container providers). Null elsewhere. */
  readonly #store = inject(EvidenceProgressStore, { optional: true });

  /** Debug gate: the card family is debug-only always (user-approved scope decision). */
  readonly #debug = inject(DebugModeService);

  /**
   * Per-episode card blocks for the visible views (array order = stacked chronological order,
   * v1.1 S7), plus the synthetic staging-pending row and the long-wait note. Empty (nothing
   * rendered) without a store, with debug mode off, or while every episode sits inside its
   * appearance debounce.
   */
  protected readonly cards = computed<EvidenceCardView[]>(() => {
    const store = this.#store;
    if (!store || !this.#debug.isDebugMode()) return [];
    const stagingSeen = store.stagingSeen();
    return store.view().map((view) => this.#cardView(view, stagingSeen));
  });

  /** One stage row from a store stage state — the running row carries the fade-in motion. */
  #rowOf(stage: IEvidenceStageView, isRunningStage: boolean): EvidenceStageRowView {
    return stageRowOf(
      stage.stage,
      stage.status,
      stage.sizeBytes,
      stage.durationMs,
      isRunningStage,
      stage.layer ?? null,
    );
  }

  /**
   * The synthetic pending-staging row (spec L45): shown while a call runs without a staging
   * row of its own, either because staging was seen in a previous call (`stagingSeen`) or
   * because the current stage has been running for ≥3 s. Never rendered once staging has a
   * real row, and never on an errored call (calm error header only).
   */
  #pendingStagingRow(view: IEvidenceBracketView, stagingSeen: boolean): EvidenceStageRowView | null {
    if (view.stages.some((stage) => stage.stage === "staging")) return null;
    const wait = view.runningStageMs;
    if (!stagingSeen && (wait == null || wait < STAGING_PRE_SHOW_MS)) return null;
    return {
      ...stageRowOf("staging", "pending", null, null, false, null),
      dimmed: true,
      tooltip: EVIDENCE_COPY.wordPending,
    };
  }

  /** Derives the full render model of one episode view (the card block). */
  #cardView(view: IEvidenceBracketView, stagingSeen: boolean): EvidenceCardView {
    const errored = view.bracket.stages.some((stage) => stage.status === "error");
    const runningStage = view.stages.find((stage) => stage.status === "running") ?? null;
    const runningIndex = runningStage ? view.stages.indexOf(runningStage) : -1;
    const summary = view.presentation === "summary";
    // v1.1 S7 data attributes: optional-in-type fields null-coalesced (legacy 9-key events and
    // hand-built fixtures may leave them undefined — they render nothing).
    const kind = view.kind ?? null;
    const repeated = (view.occurrence ?? 0) > 0;
    const repeatTooltip = EVIDENCE_COPY.repeatTooltip;

    if (errored) {
      const failedStage = view.bracket.stages.find((stage) => stage.status === "error")?.stage ?? "execute";
      const title = evidenceErrorTitle(failedStage, view.bracket.toolId);
      return {
        key: view.key,
        mode: summary ? "summary" : "running",
        errored: true,
        headerIcon: "error_outline",
        headerToneClass: "tone-err",
        headerSpin: false,
        headerTooltip: EVIDENCE_COPY.wordError,
        title,
        subtitle: EVIDENCE_COPY.stageErrorHint,
        kind,
        repeated,
        repeatTooltip,
        clock: "",
        stages: summary ? [] : view.stages.map((stage, index) => this.#rowOf(stage, index === runningIndex)),
        pendingStaging: summary ? null : this.#pendingStagingRow(view, stagingSeen),
        longNote: null,
        summaryWhat: null,
        summaryRest: null,
        a11y: title,
      };
    }

    if (summary) {
      // Summary duration chain (spec L58): execute capture → stage sums → client clock.
      const duration = formatEvidenceDuration(view.summaryDurationMs ?? view.elapsedMs);
      const rowLabel = view.bracket.rowIndex != null ? evidenceRowLabel(view.bracket.rowIndex) : null;
      const what = evidenceDoneTitle(view.bracket.toolId);
      const line = evidenceSummaryLine(what, [
        rowLabel,
        formatEvidenceBytes(view.summarySizeBytes) || null,
        duration || null,
      ]);
      return {
        key: view.key,
        mode: "summary",
        errored: false,
        headerIcon: "check_circle",
        headerToneClass: "tone-ok",
        headerSpin: false,
        headerTooltip: EVIDENCE_COPY.wordCompleted,
        // S11: the episode name never disappears on completion — the label (or its tool
        // fallback) stays the block title and the Done line renders under it.
        title: episodeTitle(view),
        subtitle: null,
        kind,
        repeated,
        repeatTooltip,
        clock: "",
        stages: [],
        pendingStaging: null,
        longNote: null,
        summaryWhat: what,
        summaryRest: line.length > what.length ? line.slice(what.length) : "",
        a11y: line, // the summary announces once when it replaces the stage list
      };
    }

    const wait = view.runningStageMs;
    return {
      key: view.key,
      mode: "running",
      errored: false,
      headerIcon: "sync",
      headerToneClass: "tone-pri",
      headerSpin: true,
      headerTooltip: EVIDENCE_COPY.wordRunning,
      // The label IS the title (v1.1 S7 — the exact evidence text); without one the round-1
      // tool-based title fallback applies. The clamp keeps long labels to 2 lines and the
      // tooltip binding carries the full text. Same derivation as the summary title (S11).
      title: episodeTitle(view),
      subtitle: evidenceSubtitle(view.bracket.taskId, view.bracket.rowIndex),
      kind,
      repeated,
      repeatTooltip,
      clock: formatEvidenceClock(view.elapsedMs),
      stages: view.stages.map((stage, index) => this.#rowOf(stage, index === runningIndex)),
      pendingStaging: this.#pendingStagingRow(view, stagingSeen),
      longNote: wait != null && wait > LONG_WAIT_MS ? EVIDENCE_COPY.stageLongHint : null,
      summaryWhat: null,
      summaryRest: null,
      // a11y (spec L105): announces ONLY the stage change, never the chronometer.
      a11y: runningStage ? evidenceStageLabel(runningStage.stage, runningStage.status) : "",
    };
  }
}
